import { CompanyMeasurementProvider } from '../context/CompanyMeasurementProvider'
import { Measure } from '../components/Measurement'
import { MeasurementInput } from '../components/MeasurementInput'
import { useState } from 'react'
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adrReferenceService, ADRClass, UNNumber, CompatibilityRule } from '../services/adrReference.service'
import {
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon,
  NoSymbolIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

// Custom Toggle Component
function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <div className={`w-11 h-6 rounded-full transition-colors ${checked ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
          <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
        </div>
      </div>
      <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
    </label>
  )
}

export default function ADRSettings() {
  return <CompanyMeasurementProvider><ADRSettingsContent /></CompanyMeasurementProvider>
}

function ADRSettingsContent() {
  const [activeTab, setActiveTab] = useState<'classes' | 'compatibility'>('classes')
  const [selectedClass, setSelectedClass] = useState<ADRClass | null>(null)
  const [showAddRuleModal, setShowAddRuleModal] = useState(false)
  const [showAddUNModal, setShowAddUNModal] = useState(false)
  const [showEditUNModal, setShowEditUNModal] = useState(false)
  const [showEditRuleModal, setShowEditRuleModal] = useState(false)
  const [editingUN, setEditingUN] = useState<UNNumber | null>(null)
  const [editingRule, setEditingRule] = useState<CompatibilityRule | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const queryClient = useQueryClient()

  // Delete UN number mutation
  const deleteUNMutation = useMutation({
    mutationFn: (id: string) => adrReferenceService.deleteUNNumber(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['un-numbers', selectedClass?.code] })
    }
  })

  // Delete compatibility rule mutation
  const deleteRuleMutation = useMutation({
    mutationFn: (id: string) => adrReferenceService.deleteCompatibilityRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compatibility-rules'] })
    }
  })

  // Fetch ADR classes
  const { data: adrClasses = [], isLoading: classesLoading, error: classesError } = useQuery({
    queryKey: ['adr-classes'],
    queryFn: () => adrReferenceService.getADRClasses()
  })

  // Fetch UN numbers for selected class
  const { data: unNumbers = [], isLoading: unLoading } = useQuery({
    queryKey: ['un-numbers', selectedClass?.code],
    queryFn: () => {
      console.log('Fetching UN numbers for class:', selectedClass?.code);
      return adrReferenceService.getUNNumbers(selectedClass?.code);
    },
    enabled: !!selectedClass
  })

  // Fetch compatibility rules
  const { data: compatibilityRules = [], isLoading: rulesLoading, error: rulesError } = useQuery({
    queryKey: ['compatibility-rules'],
    queryFn: () => adrReferenceService.getCompatibilityRules()
  })

  // Filter classes based on search
  const filteredClasses = adrClasses?.filter(cls =>
    cls.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.code.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow-lg shadow-amber-500/20">
              <ShieldCheckIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Compatibilités Classes ADR
              </h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Gestion des classes ADR, numéros UN et règles de compatibilité
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 dark:border-slate-800 mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('classes')}
              className={`${
                activeTab === 'classes'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Classes ADR & Numéros UN
            </button>
            <button
              onClick={() => setActiveTab('compatibility')}
              className={`${
                activeTab === 'compatibility'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Règles de Compatibilité
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'classes' ? (
          <ClassesTab
            classes={filteredClasses}
            loading={classesLoading}
            error={classesError}
            selectedClass={selectedClass}
            onSelectClass={setSelectedClass}
            unNumbers={unNumbers}
            unLoading={unLoading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddUN={() => setShowAddUNModal(true)}
            onEditUN={(un) => {
              setEditingUN(un)
              setShowEditUNModal(true)
            }}
            onDeleteUN={(id) => deleteUNMutation.mutate(id)}
          />
        ) : (
          <CompatibilityTab
            rules={compatibilityRules}
            loading={rulesLoading}
            error={rulesError}
            onAddRule={() => setShowAddRuleModal(true)}
            onEditRule={(rule) => {
              setEditingRule(rule)
              setShowEditRuleModal(true)
            }}
            onDeleteRule={(id) => deleteRuleMutation.mutate(id)}
          />
        )}
      </div>

      {/* Modals */}
      {showAddRuleModal && (
        <AddRuleModal
          adrClasses={adrClasses || []}
          onClose={() => setShowAddRuleModal(false)}
          onSuccess={() => {
            setShowAddRuleModal(false)
            queryClient.invalidateQueries({ queryKey: ['compatibility-rules'] })
          }}
        />
      )}

      {showAddUNModal && selectedClass && (
        <AddUNModal
          adrClass={selectedClass}
          onClose={() => setShowAddUNModal(false)}
          onSuccess={() => {
            setShowAddUNModal(false)
            queryClient.invalidateQueries({ queryKey: ['un-numbers', selectedClass.code] })
          }}
        />
      )}

      {showEditUNModal && editingUN && selectedClass && (
        <EditUNModal
          unNumber={editingUN}
          adrClass={selectedClass}
          onClose={() => {
            setShowEditUNModal(false)
            setEditingUN(null)
          }}
          onSuccess={() => {
            setShowEditUNModal(false)
            setEditingUN(null)
            queryClient.invalidateQueries({ queryKey: ['un-numbers', selectedClass.code] })
          }}
        />
      )}

      {showEditRuleModal && editingRule && (
        <EditRuleModal
          rule={editingRule}
          adrClasses={adrClasses || []}
          onClose={() => {
            setShowEditRuleModal(false)
            setEditingRule(null)
          }}
          onSuccess={() => {
            setShowEditRuleModal(false)
            setEditingRule(null)
            queryClient.invalidateQueries({ queryKey: ['compatibility-rules'] })
          }}
        />
      )}
    </div>
  )
}

// Classes Tab Component
function ClassesTab({
  classes,
  loading,
  error,
  selectedClass,
  onSelectClass,
  unNumbers,
  unLoading,
  searchTerm,
  onSearchChange,
  onAddUN,
  onEditUN,
  onDeleteUN
}: {
  classes: ADRClass[]
  loading: boolean
  error: any
  selectedClass: ADRClass | null
  onSelectClass: (cls: ADRClass) => void
  unNumbers: UNNumber[]
  unLoading: boolean
  searchTerm: string
  onSearchChange: (term: string) => void
  onAddUN: () => void
  onEditUN: (un: UNNumber) => void
  onDeleteUN: (id: string) => void
}) {
  const getRiskBadgeColor = (level: string) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
      default: return 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
          <p className="text-red-800 dark:text-red-300">Erreur lors du chargement des classes ADR</p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Classes List */}
      <div className="lg:col-span-1">
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="divide-y divide-slate-200 dark:divide-slate-800 max-h-96 overflow-y-auto">
            {classes.map((cls) => (
              <button
                key={cls.id}
                onClick={() => onSelectClass(cls)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${
                  selectedClass?.id === cls.id ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900 dark:text-white">{cls.code}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{cls.metadata.label_short || cls.label}</div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRiskBadgeColor(cls.metadata.risk_level)}`}>
                    {cls.metadata.risk_level}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Class Details */}
      <div className="lg:col-span-2">
        {selectedClass ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
            <div className="p-6 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    Classe {selectedClass.code} — {selectedClass.metadata.label_short || selectedClass.label}
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {selectedClass.description}
                  </p>
                </div>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getRiskBadgeColor(selectedClass.metadata.risk_level)}`}>
                  {selectedClass.metadata.risk_level}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Distance de séparation:</span>
                  <span className="ml-2 font-medium text-slate-900 dark:text-white">
                    <Measure value={selectedClass.metadata.default_separation_distance} from="mm" quantity="vehicleLength" />
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Manipulation spéciale:</span>
                  <span className="ml-2 font-medium text-slate-900 dark:text-white">
                    {selectedClass.metadata.requires_special_handling ? 'Oui' : 'Non'}
                  </span>
                </div>
              </div>
            </div>

            {/* UN Numbers Section */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Numéros UN associés
                </h3>
                <button
                  onClick={onAddUN}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Ajouter
                </button>
              </div>

              {unLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
                </div>
              ) : unNumbers && unNumbers.length > 0 ? (
                <div className="space-y-2">
                  {unNumbers.map((un) => (
                    <div
                      key={un.id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-slate-900 dark:text-white">{un.code}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">{un.label}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {un.metadata.packing_group && (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            Groupe {un.metadata.packing_group}
                          </span>
                        )}
                        <button
                          onClick={() => onEditUN(un)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Voulez-vous désactiver le numéro UN ${un.code} ?`)) {
                              onDeleteUN(un.id)
                            }
                          }}
                          className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Désactiver"
                        >
                          <NoSymbolIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  Aucun numéro UN associé à cette classe
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-12 text-center">
            <ShieldCheckIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              Sélectionnez une classe ADR
            </h3>
            <p className="text-slate-500 dark:text-slate-400">
              Choisissez une classe dans la liste pour voir ses détails et les numéros UN associés
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// Compatibility Tab Component
function CompatibilityTab({
  rules,
  loading,
  error,
  onAddRule,
  onEditRule,
  onDeleteRule
}: {
  rules: CompatibilityRule[]
  loading: boolean
  error: any
  onAddRule: () => void
  onEditRule: (rule: CompatibilityRule) => void
  onDeleteRule: (id: string) => void
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showSystemRules, setShowSystemRules] = useState(true)
  const [showCriticalOnly, setShowCriticalOnly] = useState(false)
  const [showActiveOnly, setShowActiveOnly] = useState(true)

  const filteredRules = rules.filter(rule => {
    const matchesSearch = searchTerm === '' ||
      rule.metadata.class1.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.metadata.class2.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rule.description && rule.description.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesSystem = showSystemRules || !rule.is_system
    const matchesCritical = !showCriticalOnly || rule.metadata.is_critical
    const matchesActive = !showActiveOnly || rule.is_active

    return matchesSearch && matchesSystem && matchesCritical && matchesActive
  })
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
          <p className="text-red-800 dark:text-red-300">Erreur lors du chargement des règles de compatibilité</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
      <div className="p-6 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Règles de Compatibilité ADR
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Configuration des incompatibilités entre classes ADR et distances de séparation
            </p>
          </div>
          <button
            onClick={onAddRule}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Nouvelle Règle
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher par classe ou description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <Toggle
              checked={showSystemRules}
              onChange={setShowSystemRules}
              label="Règles système"
            />
            <Toggle
              checked={showCriticalOnly}
              onChange={setShowCriticalOnly}
              label="Critiques uniquement"
            />
            <Toggle
              checked={showActiveOnly}
              onChange={setShowActiveOnly}
              label="Actives uniquement"
            />
          </div>
        </div>
      </div>

      <div className="divide-y divide-slate-200 dark:divide-slate-800">
        {filteredRules.map((rule) => (
          <div key={rule.id} className="p-6 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {rule.metadata.class1} + {rule.metadata.class2}
                  </h3>
                  {rule.metadata.is_critical && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                      Critique
                    </span>
                  )}
                  {rule.is_system && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400">
                      Système
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                  {rule.description || rule.metadata.reason}
                </p>
                <div className="flex items-center gap-6 text-sm">
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Distance de séparation:</span>
                    <span className="ml-2 font-medium text-slate-900 dark:text-white">
                      <Measure value={rule.metadata.separation_distance} from="mm" quantity="vehicleLength" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => onEditRule(rule)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  title="Modifier"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Voulez-vous désactiver la règle de compatibilité ${rule.metadata.class1} + ${rule.metadata.class2} ?`)) {
                      onDeleteRule(rule.id)
                    }
                  }}
                  className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Désactiver"
                >
                  <NoSymbolIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredRules.length === 0 && (
        <div className="p-12 text-center">
          <ShieldCheckIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            Aucune règle de compatibilité trouvée
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            {searchTerm || !showSystemRules || showCriticalOnly || !showActiveOnly
              ? 'Ajustez vos filtres pour voir plus de règles'
              : 'Commencez par ajouter des règles de compatibilité entre les classes ADR'}
          </p>
          <button
            onClick={onAddRule}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Nouvelle Règle
          </button>
        </div>
      )}
    </div>
  )
}

// Add Rule Modal Component
function AddRuleModal({
  adrClasses,
  onClose,
  onSuccess
}: {
  adrClasses: ADRClass[]
  onClose: () => void
  onSuccess: () => void
}) {
  const [class1, setClass1] = useState('')
  const [class2, setClass2] = useState('')
  const [separationDistance, setSeparationDistance] = useState('1000')
  const [isCritical, setIsCritical] = useState(false)
  const [reason, setReason] = useState('')
  useUnsavedChangesWarning(!!class1 || !!class2 || separationDistance !== '1000' || isCritical || !!reason)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await adrReferenceService.createCompatibilityRule({
        class1,
        class2,
        separation_distance: Number(separationDistance),
        is_critical: isCritical,
        reason
      })
      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création de la règle')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Nouvelle Règle de Compatibilité
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Classe ADR 1
              </label>
              <select
                value={class1}
                onChange={(e) => setClass1(e.target.value)}
                required
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              >
                <option value="">Sélectionner</option>
                {adrClasses.map((cls) => (
                  <option key={cls.id} value={cls.code}>
                    {cls.code} — {cls.metadata.label_short || cls.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Classe ADR 2
              </label>
              <select
                value={class2}
                onChange={(e) => setClass2(e.target.value)}
                required
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
              >
                <option value="">Sélectionner</option>
                {adrClasses.map((cls) => (
                  <option key={cls.id} value={cls.code}>
                    {cls.code} — {cls.metadata.label_short || cls.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <MeasurementInput label="Distance de séparation" quantity="vehicleLength" storageUnit="mm" value={separationDistance === '' ? null : Number(separationDistance)} onValueChange={v => setSeparationDistance(v == null ? '' : String(v))} nullable={false} min={0} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500" />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isCritical"
              checked={isCritical}
              onChange={(e) => setIsCritical(e.target.checked)}
              className="w-4 h-4 text-amber-500 rounded border-slate-300 dark:border-slate-700"
            />
            <label htmlFor="isCritical" className="text-sm text-slate-700 dark:text-slate-300">
              Marquer comme critique (risque élevé)
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Raison (optionnel)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Add UN Number Modal Component
function AddUNModal({
  adrClass,
  onClose,
  onSuccess
}: {
  adrClass: ADRClass
  onClose: () => void
  onSuccess: () => void
}) {
  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [packingGroup, setPackingGroup] = useState('')
  useUnsavedChangesWarning(!!code || !!label || !!description || !!packingGroup)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await adrReferenceService.createUNNumber({
        code,
        label,
        description,
        adr_class: adrClass.code,
        packing_group: packingGroup || undefined
      })
      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création du numéro UN')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Ajouter Numéro UN
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Numéro UN
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              placeholder="ex: UN1203"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Libellé
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              placeholder="ex: Essence"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Groupe d'emballage (optionnel)
            </label>
            <select
              value={packingGroup}
              onChange={(e) => setPackingGroup(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Non spécifié</option>
              <option value="I">Groupe I</option>
              <option value="II">Groupe II</option>
              <option value="III">Groupe III</option>
            </select>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Classe ADR: <span className="font-medium text-slate-900 dark:text-white">{adrClass.code} — {adrClass.metadata.label_short || adrClass.label}</span>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50"
            >
              {loading ? 'Ajout...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit UN Number Modal Component
function EditUNModal({
  unNumber,
  adrClass,
  onClose,
  onSuccess
}: {
  unNumber: UNNumber
  adrClass: ADRClass
  onClose: () => void
  onSuccess: () => void
}) {
  const [label, setLabel] = useState(unNumber.label)
  const [description, setDescription] = useState(unNumber.description || '')
  const [packingGroup, setPackingGroup] = useState(unNumber.metadata.packing_group || '')
  useUnsavedChangesWarning(label !== unNumber.label || description !== (unNumber.description || '') || packingGroup !== (unNumber.metadata.packing_group || ''))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await adrReferenceService.updateUNNumber(unNumber.id, {
        label,
        description,
        packing_group: packingGroup || undefined
      })
      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la modification du numéro UN')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Modifier Numéro UN
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Numéro UN
            </label>
            <input
              type="text"
              value={unNumber.code}
              disabled
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Libellé
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Groupe d'emballage (optionnel)
            </label>
            <select
              value={packingGroup}
              onChange={(e) => setPackingGroup(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Non spécifié</option>
              <option value="I">Groupe I</option>
              <option value="II">Groupe II</option>
              <option value="III">Groupe III</option>
            </select>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Classe ADR: <span className="font-medium text-slate-900 dark:text-white">{adrClass.code} — {adrClass.metadata.label_short || adrClass.label}</span>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50"
            >
              {loading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Rule Modal Component
function EditRuleModal({
  rule,
  onClose,
  onSuccess
}: {
  rule: CompatibilityRule
  adrClasses: ADRClass[]
  onClose: () => void
  onSuccess: () => void
}) {
  const [separationDistance, setSeparationDistance] = useState(rule.metadata.separation_distance.toString())
  const [isCritical, setIsCritical] = useState(rule.metadata.is_critical)
  const [reason, setReason] = useState(rule.metadata.reason || '')
  useUnsavedChangesWarning(separationDistance !== rule.metadata.separation_distance.toString() || isCritical !== rule.metadata.is_critical || reason !== (rule.metadata.reason || ''))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await adrReferenceService.updateCompatibilityRule(rule.id, {
        separation_distance: Number(separationDistance),
        is_critical: isCritical,
        reason
      })
      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la modification de la règle')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Modifier Règle de Compatibilité
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Classe ADR 1
              </label>
              <input
                type="text"
                value={rule.metadata.class1}
                disabled
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Classe ADR 2
              </label>
              <input
                type="text"
                value={rule.metadata.class2}
                disabled
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
              />
            </div>
          </div>

          <div>
            <MeasurementInput label="Distance de séparation" quantity="vehicleLength" storageUnit="mm" value={separationDistance === '' ? null : Number(separationDistance)} onValueChange={v => setSeparationDistance(v == null ? '' : String(v))} nullable={false} min={0} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500" />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isCritical"
              checked={isCritical}
              onChange={(e) => setIsCritical(e.target.checked)}
              className="w-4 h-4 text-amber-500 rounded border-slate-300 dark:border-slate-700"
            />
            <label htmlFor="isCritical" className="text-sm text-slate-700 dark:text-slate-300">
              Marquer comme critique (risque élevé)
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Raison (optionnel)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50"
            >
              {loading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
